import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import n8n from "../../assets/n8n.png";
import typebot from "../../assets/typebot.jpg";

import { makeStyles } from "@material-ui/core/styles";

import {
  Avatar,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";

import { Pencil, Trash2, Plug } from "lucide-react";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useSearch } from "../../context/Search/SearchContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const newIntegrations = [];

    queueIntegration.forEach((integration) => {
      const integrationIndex = state.findIndex((u) => u.id === integration.id);
      if (integrationIndex !== -1) {
        state[integrationIndex] = integration;
      } else {
        newIntegrations.push(integration);
      }
    });

    return [...state, ...newIntegrations];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const integrationIndex = state.findIndex((u) => u.id === queueIntegration.id);

    if (integrationIndex !== -1) {
      state[integrationIndex] = queueIntegration;
      return [...state];
    } else {
      return [queueIntegration, ...state];
    }
  }

  if (action.type === "DELETE_INTEGRATION") {
    const integrationId = action.payload;
    const integrationIndex = state.findIndex((u) => u.id === integrationId);
    if (integrationIndex !== -1) {
      state.splice(integrationIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      margin: 0,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    pageCountChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    pageActions: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    mainPaper: {
      flex: 1,
      padding: 0,
      overflow: "hidden",
      borderRadius: 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
      ...theme.scrollbarStyles,
    },
    tableScroll: {
      flex: 1,
      overflowY: "auto",
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.03)",
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    avatar: {
      width: 100,
      height: 32,
      borderRadius: 6,
    },
    nameCell: {
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.danger || "#e53935",
      "&:hover": {
        background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
        color: dsColors.dangerHover || "#c62828",
      },
    },
    actionsCell: {
      whiteSpace: "nowrap",
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: dsColors.textMuted || (isDark ? "#666" : "#999"),
      fontSize: 13.5,
    },
    typeBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.4,
      textTransform: "uppercase",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
  };
});

const QueueIntegration = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [queueIntegration, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  // Busca vem do header global (compartilhada via SearchContext)
  const { ticketSearchParam: searchParam, setTicketSearchParam, clear: clearSearch } = useSearch();

  // Limpa a busca global quando sai da página
  useEffect(() => () => clearSearch(), [clearSearch]);

  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;
  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs || !planConfigs.plan || !planConfigs.plan.useIntegrations) {
        toast.error(i18n.t("queueIntegration.messages.noPermission"));
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        try {
          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchIntegrations();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    };

    socket.on(`company-${companyId}-queueIntegration`, onQueueEvent);
    return () => {
      socket.off(`company-${companyId}-queueIntegration`, onQueueEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenUserModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(false);
  };

  const handleEditIntegration = (queueIntegration) => {
    setSelectedIntegration(queueIntegration);
    setUserModalOpen(true);
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      await api.delete(`/queueIntegration/${integrationId}`);
      toast.success(i18n.t("queueIntegration.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setTicketSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getIntegrationAvatar = (type) => {
    if (type === "n8n") return <Avatar src={n8n} className={classes.avatar} />;
    if (type === "typebot") return <Avatar src={typebot} className={classes.avatar} />;
    return <span className={classes.typeBadge}>{type}</span>;
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteIntegration(deletingUser.id)}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <IntegrationModal
        open={userModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
      />
      {user.profile === "user" ? (
        <ForbiddenPage />
      ) : (
        <>
          <div className={classes.pageHeader}>
            <div className={classes.headerLeft}>
              <span className={classes.pageIconWrap}>
                <Plug size={20} strokeWidth={1.8} />
              </span>
              <div>
                <h1 className={classes.pageTitle}>
                  {i18n.t("queueIntegration.title")}
                  <span className={classes.pageCountChip}>{queueIntegration.length}</span>
                </h1>
                <div className={classes.pageSubtitle}>
                  {i18n.t("queueIntegration.subtitle") ||
                    "Conecte fluxos automatizados externos como N8N e Typebot"}
                </div>
              </div>
            </div>
            <div className={classes.pageActions}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenUserModal}
              >
                {i18n.t("queueIntegration.buttons.add")}
              </Button>
            </div>
          </div>

          <Paper
            className={classes.mainPaper}
            variant="outlined"
            elevation={0}
            onScroll={handleScroll}
          >
            <div className={classes.tableScroll}>
              <Table size="small" className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="left" style={{ width: 130 }}>
                      {i18n.t("queueIntegration.table.type") || "Tipo"}
                    </TableCell>
                    <TableCell align="center" style={{ width: 80 }}>
                      {i18n.t("queueIntegration.table.id")}
                    </TableCell>
                    <TableCell align="left">
                      {i18n.t("queueIntegration.table.name")}
                    </TableCell>
                    <TableCell align="center" style={{ width: 120 }}>
                      {i18n.t("queueIntegration.table.actions") || "Ações"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {queueIntegration.length > 0 ? (
                    queueIntegration.map((integration) => (
                      <TableRow key={integration.id} className={classes.tableRow} hover>
                        <TableCell align="left">
                          {getIntegrationAvatar(integration.type)}
                        </TableCell>
                        <TableCell align="center">{integration.id}</TableCell>
                        <TableCell align="left" className={classes.nameCell}>
                          {integration.name}
                        </TableCell>
                        <TableCell align="center" className={classes.actionsCell}>
                          <Tooltip title={i18n.t("queueIntegration.buttons.edit") || "Editar"}>
                            <IconButton
                              size="small"
                              className={classes.iconActionBtn}
                              onClick={() => handleEditIntegration(integration)}
                            >
                              <Pencil size={16} strokeWidth={2} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={i18n.t("queueIntegration.buttons.delete") || "Excluir"}>
                            <IconButton
                              size="small"
                              className={classes.deleteActionBtn}
                              onClick={() => {
                                setConfirmModalOpen(true);
                                setDeletingUser(integration);
                              }}
                            >
                              <Trash2 size={16} strokeWidth={2} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    !loading && (
                      <TableRow>
                        <TableCell colSpan={4} className={classes.emptyState}>
                          {i18n.t("queueIntegration.empty") ||
                            "Nenhuma integração cadastrada."}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                  {loading && <TableRowSkeleton columns={4} />}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default QueueIntegration;
